import { createOrder, newNotification, setFieldValue } from 'actions';
import { useCreatePlan } from 'components/RecurringPayments/hooks/useCreatePlan';
import { ReviewPayment } from 'components/ReviewPayment/ReviewRecurringPayment/ReviewPayment';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getFieldValues,
  getRecurringOffersConfigId,
  getSelectedRecurringOffer,
  hasGatewayOrigin,
} from 'selectors';
import { isLoggedIn } from 'selectors/entities/authentication/authentication';
import { useDispatch } from 'store/configureStore';
import { useTranslations } from 'utils';
import { UserForgotPassword } from '../../UserForgotPassword/UserForgotPassword';
import { UserLogin } from '../../UserLogin/UserLogin';
import { UserSignup } from '../../UserSignup/UserSignup';

export const ReviewRecurringPayment = ({
  onNext,
  onPrev,
}: {
  onPrev: () => void;
  onNext: () => void;
}) => {
  const dispatch = useDispatch();
  const i18n = useTranslations();
  const isUserLoggedIn = useSelector(isLoggedIn);
  const { session_id: clientSessionId } = useSelector(getFieldValues);
  const requiresAuthentication = !isUserLoggedIn && !clientSessionId;

  const [currentView, setCurrentView] = useState<
    'signup' | 'login' | 'reviewAndConfirm' | 'forgotPassword'
  >(requiresAuthentication ? 'signup' : 'reviewAndConfirm');

  const [showUpdatePasswordAlert, setShowUpdatePasswordAlert] = useState(false);

  const offer = useSelector(getSelectedRecurringOffer);
  const isNonAutomated = !offer?.recurring;
  const isFromGateway = useSelector(hasGatewayOrigin);
  const configurationId = useSelector(getRecurringOffersConfigId);

  const onCreatePlanSuccess = (planId: string) => {
    dispatch(setFieldValue('planId', planId));
    onNext();
  };

  const { createPlan } = useCreatePlan({
    onSuccess: onCreatePlanSuccess,
    onError: () => {
      dispatch(
        newNotification({
          message: i18n.t('notifications.create_plan_error') as string,
          type: 'error',
        }),
      );
    },
  });

  const showLogin = useCallback(() => {
    setCurrentView('login');
  }, []);

  const showSignUp = useCallback(() => {
    setCurrentView('signup');
  }, []);

  const showReviewAndConfirm = useCallback(() => {
    setCurrentView('reviewAndConfirm');
  }, []);

  const showForgotPassword = useCallback((showUpdatePasswordAlert = false) => {
    setShowUpdatePasswordAlert(showUpdatePasswordAlert);
    setCurrentView('forgotPassword');
  }, []);

  const handleLoginPrev = useCallback(() => {
    setCurrentView('signup');
  }, []);

  const handleOnNext = useCallback(
    async (token?: string) => {
      try {
        const order = await dispatch(createOrder());
        if (isNonAutomated) {
          const payload = {
            scheduleId: offer.scheduleId,
            orderId: order.id,
            orderToken: order.token,
            locale: i18n.currentLocale,
            nonAutomated: true,
            isFromGateway,
            configurationId,
            clientSessionId: clientSessionId
              ? (clientSessionId as string)
              : undefined,
            captchaResponse: token,
          };
          createPlan(payload);
        } else {
          onNext();
        }
      } catch (_error) {
        dispatch(
          newNotification({
            message: i18n.t('notifications.create_plan_error') as string,
            type: 'error',
          }),
        );
      }
    },
    [
      dispatch,
      isNonAutomated,
      offer?.scheduleId,
      i18n,
      isFromGateway,
      configurationId,
      clientSessionId,
      createPlan,
      onNext,
    ],
  );

  const ReviewStep = useMemo(() => {
    switch (currentView) {
      case 'forgotPassword':
        return (
          <UserForgotPassword
            key="userForgotPassword"
            onLogin={showLogin}
            showUpdatePasswordAlert={showUpdatePasswordAlert}
          />
        );
      case 'login':
        return (
          <UserLogin
            key="userLogin"
            handleSignUpClick={showSignUp}
            onLoginSuccess={showReviewAndConfirm}
            resetPassword={showForgotPassword}
            onPrev={handleLoginPrev}
          />
        );
      case 'reviewAndConfirm':
        return <ReviewPayment onNext={handleOnNext} onPrev={onPrev} />;
      case 'signup':
      default:
        return (
          <UserSignup
            key="userSignup"
            handleLoginClick={showLogin}
            onSignUpSuccess={showReviewAndConfirm}
            onPrev={onPrev}
          />
        );
    }
  }, [
    currentView,
    showUpdatePasswordAlert,
    showLogin,
    showSignUp,
    showReviewAndConfirm,
    showForgotPassword,
    handleLoginPrev,
    handleOnNext,
    onPrev,
  ]);

  return ReviewStep;
};
